import { CaptionedNichePersonImage } from '@/components/controls';
import {
  useGroups,
  useNicheOccurrence,
  useSetUrgentActionComplete,
  useUser,
  useUserInfo,
} from '@/hooks';
import { listGroupLabels } from '@/utils';
import {
  Create as CreateIcon,
  Done as DoneIcon,
  Map as MapIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  IconButton,
  List,
  ListSubheader,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { OfficerInCase } from './OfficerInCase';
import { UrgentActionUpdate } from './UrgentActionUpdate';

export function UrgentAction({
  urgentAction,
  onSettingsClick,
  onAddUpdateClick,
  onShowLocationClick,
}) {
  const {
    query: { data: user },
  } = useUser(urgentAction.edited.username);
  const {
    query: { data: groups },
  } = useGroups({ types: ['SECTOR', 'TEAM'] });
  const {
    query: { data: occurrence, isLoading, isFetching },
  } = useNicheOccurrence(urgentAction.occurrenceNumber, { people: true });
  const { mutate: setUrgentActionComplete } = useSetUrgentActionComplete();
  const {
    query: {
      data: { briefingRole },
    },
  } = useUserInfo();

  function handleClick() {
    setUrgentActionComplete({
      code: urgentAction.code,
      isComplete: !urgentAction.completed,
    });
  }

  return (
    <Card variant="outlined" sx={{ minWidth: 400 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ overflowX: 'auto', px: 1, pt: 1 }}
      >
        {isLoading || isFetching ? (
          <Skeleton variant="rounded" sx={{ minWidth: 240 }} height={320} />
        ) : (
          occurrence?.people?.map((person, index) => (
            <CaptionedNichePersonImage person={person} key={index} />
          ))
        )}
      </Stack>
      <CardContent>
        <Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: 'space-between' }}
          >
            <Tooltip title="Occurrence Number" placement="left-start">
              <Typography
                variant="h6"
                color="textSecondary"
                gutterBottom
                sx={{ flexGrow: 1 }}
              >
                {occurrence?.number}
              </Typography>
            </Tooltip>
            <OfficerInCase code={occurrence?.officerCode} />
          </Stack>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            {user
              ? `${user?.displayName} - ${format(
                  new Date(urgentAction.edited.time),
                  'dd/MM/yyyy HH:mm',
                )}`
              : '-'}
          </Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }} gutterBottom>
            {urgentAction.message}
          </Typography>
        </Stack>
        <Typography gutterBottom sx={{ whiteSpace: 'pre-wrap' }}>
          {urgentAction.description}
        </Typography>
        <Divider sx={{ paddingTop: 1 }} />
        <List sx={{ maxHeight: 200, overflow: 'auto' }} disablePadding>
          <ListSubheader disableGutters>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ flexGrow: 1 }}>Updates</Box>
              {briefingRole !== 'NONE' && (
                <Box>
                  <Tooltip title="Add Update">
                    <IconButton
                      size="small"
                      data-code={urgentAction.code}
                      onClick={onAddUpdateClick}
                    >
                      <CreateIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </ListSubheader>
          {urgentAction.updates.map((update) => (
            <UrgentActionUpdate key={update.time} update={update} />
          ))}
        </List>
      </CardContent>
      <CardActions>
        <Box>
          <Button
            variant="outlined"
            size="small"
            color={urgentAction.completed?.time ? 'success' : 'warning'}
            onClick={handleClick}
            endIcon={
              urgentAction.completed?.time ? (
                <DoneIcon />
              ) : (
                <RadioButtonUncheckedIcon />
              )
            }
          >
            {urgentAction.completed?.time ? 'Completed' : 'Open'}
          </Button>
        </Box>
        <Typography
          sx={{
            paddingLeft: 1,
            '&:hover': {
              textOverflow: 'clip',
              whiteSpace: 'normal',
              wordBreak: 'break-all',
            },
            flexGrow: 1,
          }}
          noWrap
          variant="caption"
          color="textSecondary"
        >
          {!!urgentAction && !!groups
            ? `${listGroupLabels('Group', urgentAction.groupCodes, groups)}`
            : ''}
        </Typography>
        {occurrence?.addresses?.length > 0 && (
          <Tooltip title="Show map">
            <IconButton size="small" onClick={onShowLocationClick(occurrence)}>
              <MapIcon />
            </IconButton>
          </Tooltip>
        )}
        {['ADMIN', 'SUPERVISOR'].includes(briefingRole) && (
          <Box>
            <Tooltip title="Settings">
              <IconButton size="small" onClick={onSettingsClick}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </CardActions>
    </Card>
  );
}
